// External dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import {
  deletePage,
  getPages,
  getPosts,
} from '@/modules/cms/client/api/admin-cms.api';
import { isBlogCategoriesEnabled } from '@/modules/cms/client/utils/feature-flags';

const STATUS_LABELS = {
  draft: { label: 'Draft', className: 'label label-default' },
  published: { label: 'Published', className: 'label label-success' },
  archived: { label: 'Archived', className: 'label label-warning' },
};

/**
 * What differs between the two kinds this screen lists.
 */
const KIND_CONFIG = {
  page: {
    title: 'Pages',
    singular: 'page',
    intro: (
      <>
        Content pages served from <code>/pages/&lt;slug&gt;</code>.
      </>
    ),
    adminBase: '/admin/cms/pages',
    publicBase: '/pages',
    list: getPages,
  },
  post: {
    title: 'Blog posts',
    singular: 'post',
    intro: (
      <>
        Blog posts served from <code>/blog/&lt;slug&gt;</code>, newest first at{' '}
        <code>/blog</code>.
      </>
    ),
    adminBase: '/admin/cms/posts',
    publicBase: '/blog',
    list: getPosts,
  },
};

/**
 * Format a date for the listing, tolerating a missing value.
 *
 * @param {String} value - ISO date string
 * @returns {String}
 */
function formatDate(value) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString();
}

/**
 * Is this page live right now?
 *
 * Mirrors `CmsPage.publishedQuery()` on the server, so the listing does not
 * claim a scheduled page is already visible.
 *
 * @param {Object} page - Page document
 * @returns {Boolean}
 */
function isLive(page) {
  if (page.status !== 'published') {
    return false;
  }
  return !page.publishAt || new Date(page.publishAt) <= new Date();
}

/**
 * Admin listing of pages, or of blog posts when `kind` is `post`.
 */
export default function AdminCmsPages({ kind }) {
  const config = KIND_CONFIG[kind] || KIND_CONFIG.page;
  const showCategories = kind === 'post' && isBlogCategoriesEnabled();

  const [pages, setPages] = useState([]);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // `searchTerm` is what has actually been submitted. Keeping it separate
  // from the input value stops a status change from silently applying a
  // half-typed search.
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    const filters = {};
    if (status) {
      filters.status = status;
    }
    if (searchTerm) {
      filters.search = searchTerm;
    }

    config
      .list(filters)
      .then(data => {
        if (!cancelled) {
          setPages(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(`Failed to load ${config.title.toLowerCase()}.`);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [status, searchTerm, kind]);

  const handleSearch = event => {
    event.preventDefault();
    setSearchTerm(search.trim());
  };

  const handleDelete = async page => {
    const confirmed = window.confirm(
      `Move “${page.title}” to trash? You can restore it from the Trash screen.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      await deletePage(page._id);
      setPages(previous => previous.filter(item => item._id !== page._id));
    } catch (err) {
      setError(`Failed to delete ${config.singular}.`);
    }
  };

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-9">
            <h2>{config.title}</h2>
            <p>
              {config.intro}
              {showCategories && (
                <>
                  {' '}
                  <a href="/admin/cms/blog-categories">Manage categories</a>
                </>
              )}
            </p>
          </div>
          <div className="col-sm-3" style={{ marginTop: '20px' }}>
            <a
              href={`${config.adminBase}/new`}
              className="btn btn-primary btn-block"
            >
              New {config.singular}
            </a>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-3">
            <label htmlFor="admin-cms-status">Status</label>
            <select
              id="admin-cms-status"
              className="form-control"
              value={status}
              onChange={event => setStatus(event.target.value)}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="col-sm-6">
            <form onSubmit={handleSearch}>
              <label htmlFor="admin-cms-search">Search</label>
              <div className="input-group">
                <input
                  id="admin-cms-search"
                  type="text"
                  className="form-control"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Title or slug"
                />
                <span className="input-group-btn">
                  <button type="submit" className="btn btn-default">
                    Search
                  </button>
                </span>
              </div>
            </form>
          </div>
        </div>

        {isLoading ? (
          <p>Loading {config.title.toLowerCase()}…</p>
        ) : (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                {showCategories && <th>Category</th>}
                <th>Language</th>
                <th>Status</th>
                <th>{kind === 'post' ? 'Date' : 'Last updated'}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map(page => {
                // Don't shadow the `status` filter from the outer scope.
                const statusLabel =
                  STATUS_LABELS[page.status] || STATUS_LABELS.draft;

                return (
                  <tr key={page._id}>
                    <td>{page.title}</td>
                    <td>
                      <code>{page.slug}</code>
                    </td>
                    {showCategories && (
                      <td>{(page.category && page.category.name) || '—'}</td>
                    )}
                    <td>{page.locale}</td>
                    <td>
                      <span className={statusLabel.className}>
                        {statusLabel.label}
                      </span>
                      {page.status === 'published' && !isLive(page) && (
                        <span
                          className="label label-info"
                          style={{ marginLeft: '5px' }}
                          title={`Goes live ${formatDate(page.publishAt)}`}
                        >
                          Scheduled
                        </span>
                      )}
                    </td>
                    <td>
                      {formatDate(
                        kind === 'post'
                          ? page.publishAt || page.updatedAt
                          : page.updatedAt,
                      )}
                    </td>
                    <td>
                      <a
                        href={`${config.adminBase}/${page._id}`}
                        className="btn btn-primary btn-xs"
                        style={{ marginRight: '5px' }}
                      >
                        Edit
                      </a>
                      {isLive(page) && (
                        <a
                          href={`${config.publicBase}/${page.slug}`}
                          className="btn btn-default btn-xs"
                          style={{ marginRight: '5px' }}
                        >
                          View
                        </a>
                      )}
                      <button
                        type="button"
                        className="btn btn-danger btn-xs"
                        onClick={() => handleDelete(page)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!pages.length && (
                <tr>
                  <td colSpan={showCategories ? 7 : 6}>
                    No {config.title.toLowerCase()} found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

AdminCmsPages.propTypes = {
  kind: PropTypes.oneOf(['page', 'post']),
};

AdminCmsPages.defaultProps = {
  kind: 'page',
};
